<script setup lang="ts">
import { cn } from '@/lib/utils'
import { Progress } from '~/components/ui/progress'
import { Label } from '~/components/ui/label'
import { useToast } from '~/components/ui/toast'
import type { PollWidgetProps, PollItem, PollData } from './types'

const props = withDefaults(defineProps<PollWidgetProps>(), {
  showPercentages: true,
  showValues: true,
  showTitle: true
})

const config = useRuntimeConfig()
const { toast } = useToast()
const { t } = useI18n()
const isVoting = ref(false)

interface ExtendedPollItem extends PollItem {
  id: number
  type: 'option' | 'organization'
  isClickable: boolean
}

const { data: fetchedPollData } = await useApi<{ poll: PollData }>(() => `/poll/${props.pollId}`, {
  key: `poll-${props.pollId}`,
  baseURL: config.public.baseApiUrl,
  skip: !props.pollId,
  server: false
})
const handleVote = async (item: ExtendedPollItem) => {
  const currentPoll = fetchedPollData.value
  if (!currentPoll || !item.isClickable || isVoting.value) return

  isVoting.value = true
  try {
    const body = {
      poll_id: currentPoll.id,
      ...(item.type === 'option' ? { option: item.id } : { organization_id: item.id })
    }

    const response = await $fetch<{ poll: PollData }>('/polls', {
      method: 'POST',
      baseURL: config.public.baseApiUrl,
      body
    })
    if (response.poll) {
      fetchedPollData.value = response.poll
      toast({
        title: t('widget.poll.vote_success_title'),
        description: t('widget.poll.vote_success_description')
      })
    }
  } catch (error) {
    console.error('Failed to submit vote:', error)
  } finally {
    isVoting.value = false
  }
}

const modernColors = [
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#06B6D4', // cyan-500
  '#84CC16', // lime-500
  '#F97316', // orange-500
  '#EC4899', // pink-500
  '#6366F1' // indigo-500
]

const normalizedItems = computed((): ExtendedPollItem[] => {
  const currentPoll = fetchedPollData.value
  if (currentPoll) {
    const allItems: ExtendedPollItem[] = []
    let colorIndex = 0
    const canVote = !currentPoll.is_voted

    if (currentPoll.organizations && currentPoll.organizations.length > 0) {
      const organizationItems = currentPoll.organizations.map((org) => ({
        id: org.id,
        label: org.title,
        value: org.clicks_count,
        percentage: org.percent,
        color: modernColors[colorIndex++ % modernColors.length] || '#6B7280',
        type: 'organization' as const,
        isClickable: canVote
      }))
      allItems.push(...organizationItems)
    }

    if (currentPoll.options && currentPoll.options.length > 0) {
      const optionItems = currentPoll.options.map((option) => ({
        id: option.id,
        label: option.title,
        value: option.clicks_count,
        percentage: option.percent,
        color: modernColors[colorIndex++ % modernColors.length] || '#6B7280',
        type: 'option' as const,
        isClickable: canVote
      }))
      allItems.push(...optionItems)
    }

    return allItems
  }

  if (props.items) {
    const total = props.total || props.items.reduce((sum, item) => sum + item.value, 0)
    return props.items.map((item, index) => ({
      id: index,
      ...item,
      percentage: item.percentage ?? (total > 0 ? (item.value / total) * 100 : 0),
      color: item.color || modernColors[index % modernColors.length] || '#6B7280',
      type: 'option' as const,
      isClickable: false
    }))
  }

  return []
})

const computedTitle = computed(() => {
  if (props.title) return props.title
  if (fetchedPollData.value) return fetchedPollData.value.title
  if (props.pollData) return props.pollData.title
  return undefined
})

const computedTotal = computed(() => {
  if (props.total) return props.total
  if (fetchedPollData.value) return fetchedPollData.value.total_clicks_count
  if (props.pollData) return props.pollData.total_clicks_count
  if (props.items) return props.items.reduce((sum, item) => sum + item.value, 0)
  return 0
})

const itemClass = (item: ExtendedPollItem) =>
  cn(
    'rounded-xl border border-gray-100 bg-gray-50/30 p-4 transition-colors',
    item.isClickable && !isVoting.value
      ? 'cursor-pointer hover:border-blue-200 hover:bg-blue-50/50'
      : 'hover:bg-gray-50/50',
    isVoting.value && 'cursor-not-allowed opacity-60'
  )
</script>

<template>
  <div :class="cn('w-full', props.class)" class="space-y-4">
    <h3 v-if="showTitle && computedTitle" class="mb-6 text-lg font-semibold text-gray-900">
      {{ computedTitle }}
    </h3>

    <div class="space-y-4">
      <div
        v-for="item in normalizedItems"
        :key="`${item.type}-${item.id}`"
        :role="item.isClickable ? 'button' : undefined"
        :tabindex="item.isClickable ? 0 : undefined"
        :class="itemClass(item)"
        @click="handleVote(item)"
      >
        <div class="mb-3 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div
              :class="
                cn(
                  'h-3 w-3 flex-shrink-0 rounded-full',
                  item.isClickable && !isVoting.value && 'ring-2 ring-blue-200 ring-offset-1'
                )
              "
              :style="{ backgroundColor: item.color }"
            />
            <Label>
              {{ item.label }}
            </Label>
          </div>
          <div class="flex items-center gap-3 text-right">
            <span v-if="showValues" class="text-sm font-medium text-gray-600">
              {{ item.value.toLocaleString('ru-RU') }}
            </span>
            <span v-if="showPercentages" class="min-w-[3rem] text-base font-bold text-gray-900">
              {{ item.percentage?.toFixed(1) || '0.0' }}%
            </span>
          </div>
        </div>

        <div class="relative">
          <Progress
            :model-value="item.percentage || 0"
            class="mb-1 h-3 rounded-full bg-gray-200/60 [&_[data-reka-progress-indicator]]:rounded-full [&_[data-reka-progress-indicator]]:bg-blue-600 [&_[data-reka-progress-indicator]]:transition-all [&_[data-reka-progress-indicator]]:duration-700 [&_[data-reka-progress-indicator]]:ease-out"
          />
        </div>
      </div>
    </div>

    <div v-if="showValues && computedTotal > 0" class="mt-6 border-t border-gray-200 pt-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 text-sm text-gray-600">
          <div class="h-2 w-2 rounded-full bg-blue-600" />
          {{ $t('widget.poll.total_votes') }} {{ computedTotal.toLocaleString('ru-RU') }}
        </div>
        <div v-if="isVoting" class="text-sm font-medium text-blue-600">
          {{ $t('widget.poll.submitting') }}
        </div>
      </div>
    </div>
  </div>
</template>
